import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { orders, addresses, getImageUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, Plus, MapPin, Loader2, FileText, ExternalLink, Download } from 'lucide-react';

// Terms & Conditions PDF URL
const TERMS_PDF_URL = 'https://customer-assets.emergentagent.com/job_5c6f815b-a0dd-4a5d-9a84-3fa3f75d5a5b/artifacts/1ewfrium_ICX%20Terms%20%26%20Conditions_%20Credit%20application-DB.pdf';

const CheckoutPage = () => {
  usePageTitle('Checkout');
  const navigate = useNavigate();
  const { cart, fetchCart } = useCart();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderId, setOrderId] = useState(''); // Store order ID for POS download
  
  // Terms & Conditions
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  
  // Saved addresses
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('new');
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  
  // PO Number and Notes
  const [poNumber, setPoNumber] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [saveAddress, setSaveAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState('');

  const [shippingAddress, setShippingAddress] = useState({
    label: '',
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    contact_person: '',
    contact_phone: ''
  });

  useEffect(() => {
    loadSavedAddresses();
  }, []);

  const loadSavedAddresses = async () => {
    try {
      setLoadingAddresses(true);
      const response = await addresses.getAll();
      setSavedAddresses(response.data);
      
      // If there's a default address, select it
      const defaultAddr = response.data.find(a => a.is_default);
      if (defaultAddr) {
        setSelectedAddressId(defaultAddr.id);
        populateAddressForm(defaultAddr);
      }
    } catch (error) {
      console.error('Failed to load addresses:', error);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const populateAddressForm = (address) => {
    setShippingAddress({
      label: address.label || '',
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || '',
      contact_person: address.contact_person || '',
      contact_phone: address.contact_phone || ''
    });
  };

  const handleAddressSelect = (value) => {
    setSelectedAddressId(value);
    
    if (value === 'new') {
      setShippingAddress({
        label: '',
        street: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        contact_person: '',
        contact_phone: ''
      });
      setSaveAddress(false);
    } else {
      const selected = savedAddresses.find(a => a.id === value);
      if (selected) {
        populateAddressForm(selected);
        setSaveAddress(false);
      }
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate terms acceptance
    if (!acceptedTerms) {
      toast.error('Please accept the Terms & Conditions to proceed');
      return;
    }
    
    setLoading(true);

    try {
      const orderPayload = {
        shipping_address: {
          ...shippingAddress,
          label: shippingAddress.label || addressLabel || null
        },
        delivery_notes: deliveryNotes || null,
        po_number: poNumber || null,
        save_address: saveAddress && selectedAddressId === 'new',
        accepted_terms: acceptedTerms,
      };

      const response = await orders.create(orderPayload);

      setOrderNumber(response.data.order_number);
      setOrderId(response.data.id); // Store order ID for POS download
      setOrderComplete(true);
      
      // Update user credit
      const newCreditUsed = (user?.credit_used || 0) + cart.total;
      updateUser({ ...user, credit_used: newCreditUsed });
      
      // Refresh cart
      fetchCart();
      
      toast.success('Order placed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  // Download order for POS import - uses backend endpoint to fetch barcodes
  const handleDownloadForPOS = async () => {
    if (!orderId) {
      toast.error('No order data available');
      return;
    }

    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const downloadUrl = `${API_URL}/api/orders/${orderId}/download-pos`;
      
      // Fetch with auth header
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `order_${orderNumber}_pos.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Order data downloaded for POS import');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download order data');
    }
  };

  if (orderComplete) {
    return (
      <div className="container-wide py-16 text-center fade-in">
        <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
        <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Order Placed Successfully!</h1>
        <p className="text-[#64748B] mb-2">Thank you for your order.</p>
        <p className="text-lg font-mono text-[#002FA7] mb-4" data-testid="order-number">
          Order Number: {orderNumber}
        </p>
        {poNumber && (
          <p className="text-sm text-[#64748B] mb-2">
            PO Number: {poNumber}
          </p>
        )}
        <p className="text-sm text-[#64748B] mb-6 max-w-md mx-auto">
          A confirmation email has been sent to your registered email address.
          You will receive an invoice shortly. Payment can be made directly to our account.
        </p>
        
        {/* Download for POS */}
        <div className="bg-[#F0FDF4] border border-[#86EFAC] p-4 rounded-md max-w-md mx-auto mb-8">
          <p className="text-sm text-[#166534] mb-3">
            <strong>Import to your POS system?</strong><br />
            Download your order with SKUs, barcodes, and quantities.
          </p>
          <Button
            onClick={handleDownloadForPOS}
            variant="outline"
            className="gap-2 border-green-500 text-green-700 hover:bg-green-50"
            data-testid="download-pos-btn"
          >
            <Download className="w-4 h-4" />
            Download for POS Import
          </Button>
        </div>
        
        <div className="flex justify-center gap-4">
          <Button
            onClick={() => navigate('/orders')}
            className="bg-[#002FA7] hover:bg-[#0025a0]"
            data-testid="view-orders-btn"
          >
            View Orders
          </Button>
          <Button
            onClick={() => navigate('/products')}
            variant="outline"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="container-wide py-8 md:py-12 fade-in">
      <h1 className="text-3xl font-bold text-[#0F172A] mb-8">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Shipping Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="checkout-form">
            
            {/* PO Number Section */}
            <div className="border border-[#E2E8F0] p-6">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-4">Purchase Order</h2>
              <div>
                <Label htmlFor="po_number">PO Number (Optional)</Label>
                <Input
                  id="po_number"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="Enter your purchase order number"
                  className="h-12 mt-1"
                  data-testid="po-number-input"
                />
                <p className="text-xs text-[#64748B] mt-1">
                  Reference number for your internal tracking
                </p>
              </div>
            </div>

            {/* Delivery Address Section */}
            <div className="border border-[#E2E8F0] p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[#0F172A]">Delivery Address</h2>
                <MapPin className="w-5 h-5 text-[#64748B]" />
              </div>
              
              {/* Address Selection */}
              {!loadingAddresses && savedAddresses.length > 0 && (
                <div className="mb-6">
                  <Label>Select Address</Label>
                  <Select value={selectedAddressId} onValueChange={handleAddressSelect}>
                    <SelectTrigger className="h-12 mt-1" data-testid="address-select">
                      <SelectValue placeholder="Select a saved address" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">
                        <span className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          Enter New Address
                        </span>
                      </SelectItem>
                      {savedAddresses.map((addr) => (
                        <SelectItem key={addr.id} value={addr.id}>
                          <span className="flex items-center gap-2">
                            {addr.label || addr.street}
                            {addr.is_default && (
                              <span className="text-xs bg-[#002FA7] text-white px-2 py-0.5 rounded">Default</span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {loadingAddresses && (
                <div className="flex items-center gap-2 mb-6 text-[#64748B]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading saved addresses...
                </div>
              )}
              
              <div className="space-y-4">
                {selectedAddressId === 'new' && (
                  <div>
                    <Label htmlFor="label">Address Label (Optional)</Label>
                    <Input
                      id="label"
                      name="label"
                      value={shippingAddress.label}
                      onChange={handleAddressChange}
                      placeholder="e.g., Main Warehouse, Store #1"
                      className="h-12 mt-1"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="street">Street Address *</Label>
                  <Input
                    id="street"
                    name="street"
                    value={shippingAddress.street}
                    onChange={handleAddressChange}
                    placeholder="123 Business Street"
                    required
                    className="h-12 mt-1"
                    data-testid="street-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={shippingAddress.city}
                      onChange={handleAddressChange}
                      placeholder="City"
                      required
                      className="h-12 mt-1"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State/Province *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={shippingAddress.state}
                      onChange={handleAddressChange}
                      placeholder="State"
                      required
                      className="h-12 mt-1"
                      data-testid="state-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Postal Code *</Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={shippingAddress.postal_code}
                      onChange={handleAddressChange}
                      placeholder="12345"
                      required
                      className="h-12 mt-1"
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      name="country"
                      value={shippingAddress.country}
                      onChange={handleAddressChange}
                      placeholder="Country"
                      required
                      className="h-12 mt-1"
                      data-testid="country-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_person">Contact Person</Label>
                    <Input
                      id="contact_person"
                      name="contact_person"
                      value={shippingAddress.contact_person}
                      onChange={handleAddressChange}
                      placeholder="Receiving person name"
                      className="h-12 mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      name="contact_phone"
                      value={shippingAddress.contact_phone}
                      onChange={handleAddressChange}
                      placeholder="+1 555-123-4567"
                      className="h-12 mt-1"
                    />
                  </div>
                </div>

                {selectedAddressId === 'new' && (
                  <div className="flex items-center space-x-2 pt-4">
                    <Checkbox
                      id="save-address"
                      checked={saveAddress}
                      onCheckedChange={setSaveAddress}
                    />
                    <Label htmlFor="save-address" className="text-sm cursor-pointer">
                      Save this address for future orders
                    </Label>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Notes Section */}
            <div className="border border-[#E2E8F0] p-6">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-4">Delivery Notes</h2>
              <Textarea
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                placeholder="Any special delivery instructions, loading dock requirements, delivery time preferences, etc."
                rows={4}
                data-testid="notes-input"
              />
            </div>

            {/* Terms & Conditions Section */}
            <div className="border border-[#E2E8F0] p-6">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Terms & Conditions
              </h2>
              
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 mb-4 text-sm text-[#64748B] max-h-48 overflow-y-auto">
                <p className="mb-3 font-medium text-[#0F172A]">By placing this order, you agree to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Payment Terms:</strong> Payment due within 30 days from invoice date</li>
                  <li><strong>Late Fees:</strong> Up to 4% per month on overdue invoices</li>
                  <li><strong>Returns:</strong> Must be requested within 14 days, in original condition</li>
                  <li><strong>Delivery:</strong> Orders typically ship within 4 working days</li>
                  <li><strong>Ownership:</strong> Goods remain seller&apos;s property until paid in full</li>
                </ul>
              </div>
              
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="accept-terms"
                  checked={acceptedTerms}
                  onCheckedChange={setAcceptedTerms}
                  className="mt-0.5"
                  data-testid="accept-terms-checkbox"
                />
                <div>
                  <Label htmlFor="accept-terms" className="text-sm cursor-pointer">
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setTermsDialogOpen(true)}
                      className="text-[#002FA7] hover:underline font-medium inline-flex items-center gap-1"
                    >
                      Trade Terms & Conditions
                    </button>
                  </Label>
                  <p className="text-xs text-[#64748B] mt-1">
                    You must accept the terms to place an order
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="w-full h-14 bg-[#002FA7] hover:bg-[#0025a0] text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="place-order-btn"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Placing Order...</>
              ) : (
                'Place Order'
              )}
            </Button>
          </form>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="border border-[#E2E8F0] p-6 sticky top-24">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-6">Order Summary</h2>

            {/* Backorder Warning */}
            {cart.items.some(item => item.has_backorder) && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                <p className="text-sm text-amber-700 font-medium">
                  ⏳ This order contains backordered items
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Some items will ship when they become available
                </p>
              </div>
            )}

            <div className="space-y-4 mb-6 max-h-[350px] overflow-y-auto">
              {/* Group items by category */}
              {Object.entries(
                cart.items.reduce((acc, item) => {
                  const category = item.product_category || 'uncategorized';
                  if (!acc[category]) acc[category] = [];
                  acc[category].push(item);
                  return acc;
                }, {})
              ).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
                <div key={category}>
                  <p className="text-xs font-semibold text-[#002FA7] uppercase tracking-wide mb-2 capitalize">
                    {category.replace(/-/g, ' ')}
                  </p>
                  {items.map((item) => (
                    <div key={item.product_id} className="flex gap-3 border-b border-[#E2E8F0] pb-4 mb-4">
                      {/* Product Image */}
                      <div className="w-16 h-16 bg-[#F8FAFC] rounded overflow-hidden flex-shrink-0">
                        <img
                          src={getImageUrl(item.product_image) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400'}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400';
                          }}
                        />
                      </div>
                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0F172A] text-sm truncate">{item.product_name}</p>
                        <p className="text-[#64748B] text-xs font-mono">{item.product_sku}</p>
                        {item.has_backorder && (
                          <span className="inline-block text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded mt-1">
                            Backorder
                          </span>
                        )}
                        <p className="text-[#64748B] text-xs mt-1">
                          {Object.entries(item.sizes).map(([size, qty]) => `${size}: ${qty}`).join(', ')}
                        </p>
                        <p className="text-[#64748B] text-xs">
                          {item.total_quantity} units × ${item.price.toFixed(2)}
                        </p>
                      </div>
                      {/* Subtotal */}
                      <span className="font-medium text-[#0F172A] text-sm">${item.subtotal.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="border-t border-[#E2E8F0] pt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#64748B]">Subtotal ({cart.items.reduce((acc, item) => acc + item.total_quantity, 0)} units)</span>
                <span className="text-[#0F172A]">${cart.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#64748B]">GST (10%)</span>
                <span className="text-[#0F172A]">${(cart.total * 0.10).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-4">
                <span className="text-[#64748B]">Shipping</span>
                <span className="text-[#0F172A]">To be calculated</span>
              </div>
              <div className="flex justify-between pt-4 border-t border-[#E2E8F0]">
                <span className="font-semibold text-[#0F172A]">Total (inc GST)</span>
                <span className="text-2xl font-bold text-[#002FA7]" data-testid="checkout-total">
                  ${(cart.total * 1.10).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Payment Info */}
            <div className="mt-6 p-4 bg-[#F8FAFC] text-sm">
              <p className="font-medium text-[#0F172A] mb-2">Payment Terms</p>
              <p className="text-[#64748B]">
                This is a credit account order. You will receive an order confirmation email with invoice details.
              </p>
            </div>

            {/* Credit Summary */}
            <div className="mt-4 p-4 bg-[#F0FDF4] border border-[#86EFAC] text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-[#166534]">Available Credit</span>
                <span className="font-medium text-[#166534]">
                  ${((user?.credit_limit || 0) - (user?.credit_used || 0)).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#166534]">After Order (inc GST)</span>
                <span className="font-medium text-[#166534]">
                  ${((user?.credit_limit || 0) - (user?.credit_used || 0) - (cart.total * 1.10)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Dialog */}
      <Dialog open={termsDialogOpen} onOpenChange={setTermsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#002FA7]" />
              Irish Connexxion Trade Terms & Conditions
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-md">
              <p className="text-sm text-[#64748B]">
                Please review our complete Terms & Conditions below. By placing an order, you agree to be bound by these terms.
              </p>
            </div>
            
            <div className="border border-[#E2E8F0] rounded-md overflow-hidden">
              <iframe
                src={TERMS_PDF_URL}
                className="w-full h-[50vh]"
                title="Terms & Conditions PDF"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-[#E2E8F0]">
              <a
                href={TERMS_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#002FA7] hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Open PDF in new tab
              </a>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setTermsDialogOpen(false)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setAcceptedTerms(true);
                    setTermsDialogOpen(false);
                    toast.success('Terms & Conditions accepted');
                  }}
                  className="bg-[#002FA7] hover:bg-[#0025a0]"
                >
                  Accept Terms
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CheckoutPage;
